import React from 'react';
import SectionIntro from '../primitives/SectionIntro';
import HelperNote from '../primitives/HelperNote';
import TextField from '../primitives/TextField';
import SelectField from '../primitives/SelectField';
import CurrencyField from '../primitives/CurrencyField';
import PillGroup from '../primitives/PillGroup';
import AddressAutocomplete from '../primitives/AddressAutocomplete';
import RepeaterCard from '../primitives/RepeaterCard';
import ApplicantOwnership from '../primitives/ApplicantOwnership';
import CategoryCard, { ICONS } from '../primitives/CategoryCard';
import { COUNTRY_OPTIONS, countryName } from '../countries';
import { isAUAddress, combinedStreet, addressSearchHint } from '../addressRules';

const PROPERTY_USE_OPTIONS = ['Owner occupier', 'Investment'];
const STATE_OPTIONS = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

const formatCurrency = (raw) => {
  const v = parseFloat(raw);
  if (isNaN(v)) return '$0';
  return `$${v.toLocaleString('en-AU', { maximumFractionDigits: 0 })}`;
};

class StepAssets extends React.Component {
  add = (collection, builder) => {
    const next = { ...(this.props.assets[collection] || {}) };
    const idx = Object.keys(next).length + 1;
    next[idx] = builder();
    this.props.onUpdateAssets({ [collection]: next });
  };

  remove = (collection, key) => {
    const next = { ...(this.props.assets[collection] || {}) };
    delete next[key];
    this.props.onUpdateAssets({ [collection]: next });
  };

  patch = (collection, key, field, value) => {
    const items = { ...(this.props.assets[collection] || {}) };
    items[key] = { ...(items[key] || {}), [field]: value };
    this.props.onUpdateAssets({ [collection]: items });
  };

  // Apply many fields at once - used when an address autocomplete pick
  // returns Unit / Street / Suburb / State / Postcode / Country and we
  // want a single state update instead of a chain of patches.
  patchMany = (collection, key, patch) => {
    const items = { ...(this.props.assets[collection] || {}) };
    items[key] = { ...(items[key] || {}), ...patch };
    this.props.onUpdateAssets({ [collection]: items });
  };

  // Rebuild the combined `address` string from the split parts (used by
  // the legacy print view and review screens). Overseas properties have no
  // split parts, so the Google Places formatted address is the address.
  rebuildPropertyAddress = (next) => {
    if (!isAUAddress(next.country)) {
      return next.address_search || next.address || '';
    }
    const street = combinedStreet(next);
    const tail = [next.suburb, next.state, next.postcode]
      .filter(Boolean)
      .join(', ');
    const countryLabel = next.country ? countryName(next.country) : '';
    return [street, tail, countryLabel].filter(Boolean).join(', ');
  };

  patchPropertyAddressPiece = (key, field, value) => {
    const items = { ...(this.props.assets.properties || {}) };
    const merged = { ...(items[key] || {}), [field]: value };
    merged.address = this.rebuildPropertyAddress(merged);
    items[key] = merged;
    this.props.onUpdateAssets({ properties: items });
  };

  applyPropertyAutocomplete = (key, selection) => {
    const items = { ...(this.props.assets.properties || {}) };
    const cur = items[key] || {};
    const merged = {
      ...cur,
      address_search: selection.formatted || '',
      country: selection.country || cur.country || 'au',
      unit_number: selection.unit_number || '',
      street_number: selection.street_number || '',
      street_name: selection.street_name || '',
      suburb: selection.suburb || '',
      state: selection.state || '',
      postcode: selection.postcode || '',
    };
    merged.address = this.rebuildPropertyAddress(merged);
    items[key] = merged;
    this.props.onUpdateAssets({ properties: items });
  };

  renderAdd = (label, onClick) => (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-xl border-2 border-dashed py-3 text-sm font-semibold"
      style={{ borderColor: '#7C5CFC', color: '#5B3FE0', backgroundColor: '#FFFFFF' }}
    >
      + Add {label}
    </button>
  );

  renderEmpty = (text) => (
    <div className="rounded-2xl border-2 border-dashed border-[#D1D5DB] bg-[#F9FAFB] p-5 text-center">
      <p className="text-sm text-[#6B7280]">{text}</p>
    </div>
  );

  // Renders the standard ownership block (applicants + optional Other text)
  // shared by every asset repeater.
  renderOwnership = (collection, key, item, label) => {
    const errs = this.props.errors || {};
    const fieldId = `assets.${collection}.${key}.ownership`;
    return (
      <>
        <ApplicantOwnership
          applicants={this.props.applicants}
          label={label || 'Owned by'}
          required
          value={item.ownership}
          onChange={(v) => this.patch(collection, key, 'ownership', v)}
          fieldId={fieldId}
          error={errs[fieldId]}
        />
        <TextField
          label="Other (optional)"
          placeholder="e.g. company, trust, guarantor 50% owner"
          value={item.held_other_text}
          onChange={(v) => this.patch(collection, key, 'held_other_text', v)}
        />
      </>
    );
  };

  // Render the "Loans secured by this property" picker. Loans come from the
  // Liabilities section; each loan is identified by a stable id stored on
  // the loan record itself.
  renderLoanLinks = (propertyKey, property) => {
    const loans = this.props.loans || {};
    const loanEntries = Object.values(loans);
    const linked = Array.isArray(property.linked_loan_ids) ? property.linked_loan_ids : [];

    return (
      <div className="rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-3 sm:p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">
              Loans secured by this property
            </p>
            <p className="text-xs text-[#6B7280] mt-0.5">
              Tick any of your declared loans that are secured against this property.
            </p>
          </div>
          {this.props.onJumpToLiabilities ? (
            <button
              type="button"
              onClick={this.props.onJumpToLiabilities}
              className="text-xs font-semibold whitespace-nowrap underline"
              style={{ color: '#5B3FE0' }}
            >
              Edit loans
            </button>
          ) : null}
        </div>

        {loanEntries.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-[#D1D5DB] bg-white px-3 py-3 text-center">
            <p className="text-xs text-[#6B7280]">
              No loans declared yet. Add them in the Liabilities section
              {this.props.onJumpToLiabilities ? (
                <>
                  {' '}-{' '}
                  <button
                    type="button"
                    onClick={this.props.onJumpToLiabilities}
                    className="font-semibold underline"
                    style={{ color: '#5B3FE0' }}
                  >
                    go there now
                  </button>
                </>
              ) : null}
              .
            </p>
          </div>
        ) : (
          <ul className="space-y-1.5">
            {loanEntries.map((loan, i) => {
              const isLinked = linked.includes(loan.id);
              const label = loan.bank_name ? loan.bank_name : `Loan ${i + 1}`;
              return (
                <li key={loan.id}>
                  <button
                    type="button"
                    onClick={() => {
                      const next = isLinked
                        ? linked.filter((id) => id !== loan.id)
                        : [...linked, loan.id];
                      this.patch('properties', propertyKey, 'linked_loan_ids', next);
                    }}
                    className="w-full flex items-center gap-3 rounded-lg border-2 px-3 py-2 text-left transition-colors"
                    style={{
                      borderColor: isLinked ? '#7C5CFC' : '#D1D5DB',
                      backgroundColor: isLinked ? '#EFEAFE' : '#FFFFFF',
                      color: isLinked ? '#5B3FE0' : '#1F2937',
                    }}
                  >
                    <span
                      className="inline-flex items-center justify-center h-5 w-5 rounded border flex-shrink-0"
                      style={{
                        borderColor: isLinked ? '#7C5CFC' : '#9CA3AF',
                        backgroundColor: isLinked ? '#7C5CFC' : '#FFFFFF',
                      }}
                    >
                      {isLinked ? (
                        <svg className="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                          <path
                            fillRule="evenodd"
                            d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4l3.8 3.8 6.8-6.8a1 1 0 011.4 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : null}
                    </span>
                    <div className="flex-1 min-w-0 text-sm">
                      <span className="font-medium truncate">{label}</span>
                      <span className="ml-2 text-xs text-[#6B7280]">
                        owing {formatCurrency(loan.owing)}
                      </span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  render() {
    const { assets, applicants, onUpdateAssets } = this.props;

    const properties = assets.properties || {};
    const savings = assets.savings || {};
    const shares = assets.shares || {};
    const supers = assets.supers || {};
    const vehicles = assets.vehicles || {};
    const others = assets.other_assets || {};

    return (
      <div className="space-y-5">
        {/* Intro card */}
        <div className="rounded-2xl bg-white shadow-sm border border-[#E5E7EB] p-5 sm:p-8">
          <SectionIntro
            eyebrow="About your finances - Assets"
            title="What everyone owns"
            description="Include all assets owned by any applicant - jointly or solely. We use this to assess deposit, equity and overall position."
          />
          <HelperNote>
            <strong>Tip:</strong> Tick every applicant who owns each asset. Use the address search for
            properties so the formatting is consistent.
          </HelperNote>
        </div>

        {/* Real estate */}
        <CategoryCard
          icon={ICONS.realEstate}
          title="Real estate"
          description="Your home, investment properties or land. Link each property to the loans secured against it."
          count={Object.keys(properties).length}
        >
          {Object.keys(properties).length === 0
            ? this.renderEmpty('No properties added yet. Add your primary home, investment properties or land.')
            : null}
          {Object.keys(properties).map((key, i) => {
            const p = properties[key];
            const errs = this.props.errors || {};
            const country = (p.country || 'au').toLowerCase();
            const isAU = isAUAddress(country);
            return (
              <RepeaterCard
                key={key}
                title={`Property ${i + 1}`}
                subtitle={p.address || ''}
                onRemove={() => this.remove('properties', key)}
              >
                <SelectField
                  label="Country"
                  required
                  options={COUNTRY_OPTIONS}
                  value={country}
                  onChange={(v) => this.patchPropertyAddressPiece(key, 'country', v)}
                  fieldId={`assets.properties.${key}.country`}
                  error={errs[`assets.properties.${key}.country`]}
                  hint={`The autocomplete below will search ${countryName(country)} addresses.`}
                />
                <AddressAutocomplete
                  label="Search address"
                  required
                  value={p.address_search || ''}
                  country={country}
                  onChange={(v) => this.patch('properties', key, 'address_search', v)}
                  onSelect={(sel) => this.applyPropertyAutocomplete(key, sel)}
                  fieldId={`assets.properties.${key}.address_search`}
                  error={errs[`assets.properties.${key}.address_search`]}
                  hint={addressSearchHint(country)}
                />

                {/* Overseas properties skip the split street fields. See
                    addressRules.js for why. */}
                {isAU ? (
                  <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_2fr] gap-3">
                    <TextField
                      label="Unit / apt"
                      value={p.unit_number}
                      onChange={(v) => this.patchPropertyAddressPiece(key, 'unit_number', v)}
                      fieldId={`assets.properties.${key}.unit_number`}
                    />
                    <TextField
                      label="Street #"
                      required
                      inputMode="numeric"
                      value={p.street_number}
                      onChange={(v) => this.patchPropertyAddressPiece(key, 'street_number', v)}
                      fieldId={`assets.properties.${key}.street_number`}
                      error={errs[`assets.properties.${key}.street_number`]}
                    />
                    <TextField
                      label="Street name"
                      required
                      value={p.street_name}
                      onChange={(v) => this.patchPropertyAddressPiece(key, 'street_name', v)}
                      fieldId={`assets.properties.${key}.street_name`}
                      error={errs[`assets.properties.${key}.street_name`]}
                    />
                  </div>
                ) : null}

                <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr] gap-3">
                  <TextField
                    label={isAU ? 'Suburb' : 'City / suburb'}
                    required
                    value={p.suburb}
                    onChange={(v) => this.patchPropertyAddressPiece(key, 'suburb', v)}
                    fieldId={`assets.properties.${key}.suburb`}
                    error={errs[`assets.properties.${key}.suburb`]}
                  />
                  {isAU ? (
                    <SelectField
                      label="State"
                      required
                      placeholder="Select"
                      options={STATE_OPTIONS}
                      value={p.state}
                      onChange={(v) => this.patchPropertyAddressPiece(key, 'state', v)}
                      fieldId={`assets.properties.${key}.state`}
                      error={errs[`assets.properties.${key}.state`]}
                    />
                  ) : (
                    // Not every country has states or provinces.
                    <TextField
                      label="State / province (optional)"
                      value={p.state}
                      onChange={(v) => this.patchPropertyAddressPiece(key, 'state', v)}
                      fieldId={`assets.properties.${key}.state`}
                      error={errs[`assets.properties.${key}.state`]}
                    />
                  )}
                  <TextField
                    label={isAU ? 'Postcode' : 'Postal code (optional)'}
                    required={isAU}
                    inputMode={isAU ? 'numeric' : 'text'}
                    maxLength={isAU ? 4 : 12}
                    value={p.postcode}
                    onChange={(v) => {
                      const cleaned = isAU ? (v || '').replace(/\D/g, '') : v;
                      this.patchPropertyAddressPiece(key, 'postcode', cleaned);
                    }}
                    fieldId={`assets.properties.${key}.postcode`}
                    error={errs[`assets.properties.${key}.postcode`]}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1F2937] mb-2">
                    How is this property used? <span className="text-[#DC2626]">*</span>
                  </label>
                  <PillGroup
                    columns={2}
                    options={PROPERTY_USE_OPTIONS}
                    value={p.property_use}
                    onChange={(v) => this.patch('properties', key, 'property_use', v)}
                    fieldId={`assets.properties.${key}.property_use`}
                    error={errs[`assets.properties.${key}.property_use`]}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <CurrencyField
                    label="Estimated value"
                    required
                    value={p.value}
                    onChange={(v) => this.patch('properties', key, 'value', v)}
                    fieldId={`assets.properties.${key}.value`}
                    error={errs[`assets.properties.${key}.value`]}
                  />
                  <CurrencyField
                    label="Rental income"
                    required={p.property_use === 'Investment'}
                    value={p.rental_income}
                    onChange={(v) => this.patch('properties', key, 'rental_income', v)}
                    suffix="/week"
                    fieldId={`assets.properties.${key}.rental_income`}
                    error={errs[`assets.properties.${key}.rental_income`]}
                    hint={
                      p.property_use === 'Investment'
                        ? 'Required for investment properties.'
                        : 'Optional for owner-occupier properties.'
                    }
                  />
                </div>
                {this.renderOwnership('properties', key, p)}
                {this.renderLoanLinks(key, p)}
              </RepeaterCard>
            );
          })}
          {this.renderAdd('a property', () => this.add('properties', () => ({
            address: '',
            address_search: '',
            country: 'au',
            unit_number: '',
            street_number: '',
            street_name: '',
            suburb: '',
            state: '',
            postcode: '',
            property_use: '',
            value: '',
            rental_income: '',
            held_other_text: '',
            ownership: {},
            linked_loan_ids: [],
          })))}
        </CategoryCard>

        {/* Home contents */}
        <CategoryCard
          icon={ICONS.home}
          title="Home contents"
          description="Approximate total value of furniture, appliances and household items."
        >
          <CurrencyField
            label="Total value of household contents"
            required
            value={assets.home_contents_value}
            onChange={(v) => onUpdateAssets({ home_contents_value: v })}
            fieldId="assets.home_contents_value"
            error={(this.props.errors || {})['assets.home_contents_value']}
          />
        </CategoryCard>

        {/* Savings */}
        <CategoryCard
          icon={ICONS.savings}
          title="Savings accounts"
          description="Transaction, savings or term-deposit accounts."
          count={Object.keys(savings).length}
        >
          {Object.keys(savings).length === 0
            ? this.renderEmpty('Add transaction, savings or term-deposit accounts.')
            : null}
          {Object.keys(savings).map((key, i) => {
            const s = savings[key];
            const errs = this.props.errors || {};
            return (
              <RepeaterCard
                key={key}
                title={`Savings ${i + 1}`}
                subtitle={s.name || ''}
                onRemove={() => this.remove('savings', key)}
              >
                <TextField
                  label="Account / institution"
                  required
                  value={s.name}
                  onChange={(v) => this.patch('savings', key, 'name', v)}
                  fieldId={`assets.savings.${key}.name`}
                  error={errs[`assets.savings.${key}.name`]}
                />
                <CurrencyField
                  label="Current balance"
                  required
                  value={s.value}
                  onChange={(v) => this.patch('savings', key, 'value', v)}
                  fieldId={`assets.savings.${key}.value`}
                  error={errs[`assets.savings.${key}.value`]}
                />
                {this.renderOwnership('savings', key, s)}
              </RepeaterCard>
            );
          })}
          {this.renderAdd('a savings account', () => this.add('savings', () => ({
            name: '', value: '', held_other_text: '', ownership: {},
          })))}
        </CategoryCard>

        {/* Shares */}
        <CategoryCard
          icon={ICONS.shares}
          title="Shares & managed funds"
          description="Listed shares, ETFs and managed fund holdings."
          count={Object.keys(shares).length}
        >
          {Object.keys(shares).length === 0
            ? this.renderEmpty('Add listed shares, ETFs or managed fund holdings.')
            : null}
          {Object.keys(shares).map((key, i) => {
            const s = shares[key];
            const errs = this.props.errors || {};
            return (
              <RepeaterCard
                key={key}
                title={`Holding ${i + 1}`}
                subtitle={s.name || ''}
                onRemove={() => this.remove('shares', key)}
              >
                <TextField
                  label="Share / fund name"
                  required
                  value={s.name}
                  onChange={(v) => this.patch('shares', key, 'name', v)}
                  fieldId={`assets.shares.${key}.name`}
                  error={errs[`assets.shares.${key}.name`]}
                />
                <CurrencyField
                  label="Current value"
                  required
                  value={s.value}
                  onChange={(v) => this.patch('shares', key, 'value', v)}
                  fieldId={`assets.shares.${key}.value`}
                  error={errs[`assets.shares.${key}.value`]}
                />
                {this.renderOwnership('shares', key, s)}
              </RepeaterCard>
            );
          })}
          {this.renderAdd('a share or fund', () => this.add('shares', () => ({
            name: '', value: '', held_other_text: '', ownership: {},
          })))}
        </CategoryCard>

        {/* Super */}
        <CategoryCard
          icon={ICONS.superFund}
          title="Superannuation"
          description="Add each applicant's super fund balance."
          count={Object.keys(supers).length}
        >
          {Object.keys(supers).length === 0
            ? this.renderEmpty('Add each applicant\'s super fund balance.')
            : null}
          {Object.keys(supers).map((key, i) => {
            const s = supers[key];
            const errs = this.props.errors || {};
            return (
              <RepeaterCard
                key={key}
                title={`Super ${i + 1}`}
                subtitle={s.name || ''}
                onRemove={() => this.remove('supers', key)}
              >
                <TextField
                  label="Fund name"
                  required
                  value={s.name}
                  onChange={(v) => this.patch('supers', key, 'name', v)}
                  fieldId={`assets.supers.${key}.name`}
                  error={errs[`assets.supers.${key}.name`]}
                />
                <CurrencyField
                  label="Current balance"
                  required
                  value={s.value}
                  onChange={(v) => this.patch('supers', key, 'value', v)}
                  fieldId={`assets.supers.${key}.value`}
                  error={errs[`assets.supers.${key}.value`]}
                />
                {this.renderOwnership('supers', key, s, 'Whose super is this?')}
              </RepeaterCard>
            );
          })}
          {this.renderAdd('a super fund', () => this.add('supers', () => ({
            name: '', value: '', held_other_text: '', ownership: {},
          })))}
        </CategoryCard>

        {/* Vehicles */}
        <CategoryCard
          icon={ICONS.vehicles}
          title="Vehicles"
          description="Cars, motorcycles, boats or other vehicles."
          count={Object.keys(vehicles).length}
        >
          {Object.keys(vehicles).length === 0
            ? this.renderEmpty('Add cars, motorcycles, boats or other vehicles.')
            : null}
          {Object.keys(vehicles).map((key, i) => {
            const v = vehicles[key];
            const errs = this.props.errors || {};
            return (
              <RepeaterCard
                key={key}
                title={`Vehicle ${i + 1}`}
                subtitle={[v.year, v.make, v.model].filter(Boolean).join(' ')}
                onRemove={() => this.remove('vehicles', key)}
              >
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <TextField
                    label="Make"
                    required
                    value={v.make}
                    onChange={(val) => this.patch('vehicles', key, 'make', val)}
                    fieldId={`assets.vehicles.${key}.make`}
                    error={errs[`assets.vehicles.${key}.make`]}
                  />
                  <TextField
                    label="Model"
                    required
                    value={v.model}
                    onChange={(val) => this.patch('vehicles', key, 'model', val)}
                    fieldId={`assets.vehicles.${key}.model`}
                    error={errs[`assets.vehicles.${key}.model`]}
                  />
                  <TextField
                    label="Year"
                    inputMode="numeric"
                    maxLength={4}
                    value={v.year}
                    onChange={(val) => this.patch('vehicles', key, 'year', val.replace(/\D/g, ''))}
                  />
                </div>
                <CurrencyField
                  label="Estimated value"
                  required
                  value={v.value}
                  onChange={(val) => this.patch('vehicles', key, 'value', val)}
                  fieldId={`assets.vehicles.${key}.value`}
                  error={errs[`assets.vehicles.${key}.value`]}
                />
                {this.renderOwnership('vehicles', key, v)}
              </RepeaterCard>
            );
          })}
          {this.renderAdd('a vehicle', () => this.add('vehicles', () => ({
            make: '', model: '', year: '', value: '', held_other_text: '', ownership: {},
          })))}
        </CategoryCard>

        {/* Other assets */}
        <CategoryCard
          icon={ICONS.other}
          title="Other assets"
          description="Jewellery, art, business interests, equipment - anything else of value."
          count={Object.keys(others).length}
        >
          {Object.keys(others).length === 0
            ? this.renderEmpty('Jewellery, art, business interests, equipment - anything of value.')
            : null}
          {Object.keys(others).map((key, i) => {
            const o = others[key];
            const errs = this.props.errors || {};
            return (
              <RepeaterCard
                key={key}
                title={`Other asset ${i + 1}`}
                subtitle={o.name || ''}
                onRemove={() => this.remove('other_assets', key)}
              >
                <TextField
                  label="Asset name"
                  required
                  value={o.name}
                  onChange={(v) => this.patch('other_assets', key, 'name', v)}
                  fieldId={`assets.other_assets.${key}.name`}
                  error={errs[`assets.other_assets.${key}.name`]}
                />
                <CurrencyField
                  label="Estimated value"
                  required
                  value={o.value}
                  onChange={(v) => this.patch('other_assets', key, 'value', v)}
                  fieldId={`assets.other_assets.${key}.value`}
                  error={errs[`assets.other_assets.${key}.value`]}
                />
                {this.renderOwnership('other_assets', key, o)}
              </RepeaterCard>
            );
          })}
          {this.renderAdd('another asset', () => this.add('other_assets', () => ({
            name: '', value: '', held_other_text: '', ownership: {},
          })))}
        </CategoryCard>
      </div>
    );
  }
}

export default StepAssets;
